import { useState, useEffect, CSSProperties, ReactNode } from 'react';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DragIndicatorRounded as DragIndicatorIcon } from '@material-ui/icons';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ArchivableRowActions } from '../../../shared/components/Tables/ArchivableRowActions.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { Category } from '../../../categories/types/category.type';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';

interface SubCategoriesTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
}

interface SubCategoriesTableProps {
  subCategories: SubCategory[];
  categories: Category[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  isDragEnabled: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onToggleActive: (subCategoryId: string, isActive: boolean) => Promise<boolean>;
  onPositionsChange?: (subCategories: { id: string; position: number }[]) => void;
  onEdit: (subCategory: SubCategory) => void;
  onArchive: (subCategory: SubCategory) => void;
  onRestore: (subCategory: SubCategory) => void;
}

type SubCategoryRowProps = Pick<SubCategoriesTableProps, 'onEdit' | 'onArchive' | 'onRestore'> & {
  subCategory: SubCategory;
  categoryName: string;
  onToggleActive: (subCategoryId: string, isActive: boolean) => void;
};

const BASE_HEADERS: SubCategoriesTableHeader[] = [
  { key: 'name', label: 'Nom', minWidth: 200, align: 'left' },
  { key: 'description', label: 'Description', minWidth: 250, align: 'left' },
  { key: 'category', label: 'Rubrique', minWidth: 150, align: 'left' },
  { key: 'active', label: 'Statut', minWidth: 80, align: 'center' },
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 90, align: 'right' },
];

function getHeaders(isDragEnabled: boolean): SubCategoriesTableHeader[] {
  if (!isDragEnabled) return BASE_HEADERS;

  const orderHeader: SubCategoriesTableHeader = { key: 'order', label: '', minWidth: 80, align: 'left' };
  return [orderHeader, ...BASE_HEADERS];
}

const useStyles = makeStyles((theme) => ({
  positionChip: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    borderRadius: 4,
  },
  drag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    gap: '0.4rem',
  },
  dragHandle: {
    cursor: 'grab',
    opacity: 0.6,
  },
  statusChip: {
    cursor: 'pointer',
    '& > .MuiChip-root': {
      cursor: 'pointer',
    },
  },
}));

function SubCategoryStatusCell({
  subCategory,
  onToggleActive,
}: {
  subCategory: SubCategory;
  onToggleActive: (subCategoryId: string, isActive: boolean) => void;
}) {
  const classes = useStyles();

  if (subCategory.archivedAt) {
    return (
      <TableCell align="center">
        <StatusChip isActive={subCategory.isActive} archived />
      </TableCell>
    );
  }

  return (
    <TableCell align="center">
      <span className={classes.statusChip} onClick={() => onToggleActive(subCategory.id, !subCategory.isActive)}>
        <StatusChip isActive={subCategory.isActive} />
      </span>
    </TableCell>
  );
}

function SubCategoryRow({
  subCategory,
  categoryName,
  onToggleActive,
  onEdit,
  onArchive,
  onRestore,
  dragCell,
  rowRef,
  rowStyle,
}: SubCategoryRowProps & {
  dragCell?: ReactNode;
  rowRef?: (element: HTMLElement | null) => void;
  rowStyle?: CSSProperties;
}) {
  return (
    <TableRow ref={rowRef} style={rowStyle} hover tabIndex={-1}>
      {dragCell}
      <TableCell align="left">{subCategory.name}</TableCell>
      <TableCell align="left">{subCategory.description}</TableCell>
      <TableCell align="left">{categoryName}</TableCell>
      <SubCategoryStatusCell subCategory={subCategory} onToggleActive={onToggleActive} />
      <TableCell align="right">
        <ArchivableRowActions
          rowId={subCategory.id}
          isArchived={Boolean(subCategory.archivedAt)}
          onEdit={() => onEdit(subCategory)}
          onArchive={() => onArchive(subCategory)}
          onRestore={() => onRestore(subCategory)}
        />
      </TableCell>
    </TableRow>
  );
}

function SortableSubCategoryRow(props: SubCategoryRowProps) {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.subCategory.id,
  });

  const dragCell = (
    <TableCell align="left">
      <div className={classes.drag}>
        <DragIndicatorIcon {...attributes} {...listeners} className={classes.dragHandle} />
        <Chip className={classes.positionChip} size="small" label={props.subCategory.position} />
      </div>
    </TableCell>
  );

  return (
    <SubCategoryRow
      {...props}
      dragCell={dragCell}
      rowRef={setNodeRef}
      rowStyle={{ transform: CSS.Transform.toString(transform), transition }}
    />
  );
}

function SubCategoriesTable({
  subCategories,
  categories,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  isDragEnabled,
  onPageChange,
  onRowsPerPageChange,
  onToggleActive,
  onPositionsChange,
  onEdit,
  onArchive,
  onRestore,
}: SubCategoriesTableProps) {
  const [localSubCategories, setLocalSubCategories] = useState(subCategories);

  useEffect(() => {
    setLocalSubCategories(subCategories);
  }, [subCategories]);

  const handleToggleActive = async (subCategoryId: string, isActive: boolean) => {
    const success = await onToggleActive(subCategoryId, isActive);
    if (success) {
      setLocalSubCategories((previous) =>
        previous.map((subCategory) => (subCategory.id === subCategoryId ? { ...subCategory, isActive } : subCategory))
      );
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localSubCategories.findIndex((subCategory) => subCategory.id === active.id);
    const newIndex = localSubCategories.findIndex((subCategory) => subCategory.id === over.id);

    const reordered = arrayMove(localSubCategories, oldIndex, newIndex);
    const withPositions = reordered.map((subCategory, index) => ({
      ...subCategory,
      position: page * rowsPerPage + index + 1,
    }));

    setLocalSubCategories(withPositions);
    onPositionsChange?.(withPositions.map((subCategory) => ({ id: subCategory.id, position: subCategory.position })));
  };

  const getCategoryName = (categoryId: string) =>
    categories.find((category) => category.id === categoryId)?.name ?? '-';

  const rowProps = (subCategory: SubCategory): SubCategoryRowProps => ({
    subCategory,
    categoryName: getCategoryName(subCategory.category),
    onToggleActive: handleToggleActive,
    onEdit,
    onArchive,
    onRestore,
  });

  const headers = getHeaders(isDragEnabled);
  const columnCount = headers.length;

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header.key}
                  aria-label={header.ariaLabel}
                  align={header.align}
                  style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={columnCount} />}
          {!isLoading && (
            <TableBody>
              {localSubCategories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} style={{ textAlign: 'center' }}>
                    Aucune sous-rubrique
                  </TableCell>
                </TableRow>
              )}
              {isDragEnabled ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={localSubCategories.map((subCategory) => subCategory.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {localSubCategories.map((subCategory) => (
                      <SortableSubCategoryRow key={subCategory.id} {...rowProps(subCategory)} />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                localSubCategories.map((subCategory) => (
                  <SubCategoryRow key={subCategory.id} {...rowProps(subCategory)} />
                ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}

export default SubCategoriesTable;
